
import { Component, Vue, Watch } from "vue-property-decorator";
import Scan from "@/components/Scan.vue";
import TagList from "@/components/shared/TagList.vue";
import BatchRefInput from "@/components/BatchRefInput.vue";
import BatchQuantityInput from "@/components/BatchQuantityInput.vue";
import KraalSelectInput from "@/components/KraalSelectInput.vue";
import NotesInput from "@/components/NotesInput.vue";
import AverageMassInput from "@/components/AverageMassInput.vue";
import VaccinationSelectInput from "@/components/VaccinationSelectInput.vue";
import TotalTagsScanned from "@/components/TotalTagsScanned.vue";
import { withdrawalDaysFromToday } from "@/helpers/withdrawal";
import { toKraalInfoBullet } from "@/helpers/ui-info-text";
import * as Models from "@gigalot/data-models";
import lodash from "lodash";

@Component({
  components: {
    Scan,
    TagList,
    BatchRefInput,
    BatchQuantityInput,
    KraalSelectInput,
    NotesInput,
    AverageMassInput,
    VaccinationSelectInput,
    TotalTagsScanned,
  },
})
export default class GroupVaccination extends Vue {
  toKraalInfoBullet = toKraalInfoBullet;

  batchNumber: string = "";
  batchQuantity: string = "";
  kraalId: string = "";
  kraalIds: string[] = [];
  notes: string = "";
  vaccinations: Models.Vaccination[] = [];
  selectedVaccination: Models.Vaccination | null = null;
  useAverageMass: boolean = false;
  averageMass: string = "";
  appliedDoses: Models.AppliedDose[] = [];
  isUiLocked: boolean = false;

  get shownVaccinations(): Models.Vaccination[] {
    if (this.useAverageMass) {
      // return vaccinations that have at least one cc-mass-dependent dose
      return this.vaccinations.filter((v) => (v.doses ?? []).find((d) => d.type === "cc-mass-dependent"));
    } else {
      // not using mass, only return vaccinations with doses that don't depend on mass for dosage
      return this.vaccinations.filter((v) => !(v.doses ?? []).find((d) => d.type === "cc-mass-dependent"));
    }
  }

  isAverageMassNumber(): boolean {
    const value = Number(this.averageMass);
    return Number.isFinite(value);
  }

  isAverageMassPositive(): boolean {
    const value = Number(this.averageMass);
    return Number.isFinite(value) && value > 0;
  }

  isAverageMassValid(): boolean {
    return this.isAverageMassPositive();
  }

  getAverageMass(): number | undefined {
    const value = Number(this.averageMass);
    if (!Number.isFinite(value) || value <= 0) return undefined;
    return value;
  }

  async created() {
    this.$store.commit("navFuncs", {
      save: undefined,
      back: () => {
        this.$router.go(-1);
      },
    });

    this.vaccinations = await this.$store.dispatch("data/getVaccinations");
  }

  onClearWhenSaved() {
    this.batchNumber = "";
    this.batchQuantity = "";
    this.kraalId = "";
    this.notes = "";
    this.selectedVaccination = null;
    this.useAverageMass = false;
    this.averageMass = "";
    this.$store.commit("groupVaccination/batchQuantity", "");
    this.$store.commit("groupVaccination/batchNumber", "");
    this.$store.commit("groupVaccination/kraalId", "");
    this.$store.commit("groupVaccination/notes", "");
    this.$store.commit("groupVaccination/averageMass", undefined);
  }

  sgtins: string[] = [];
  animals: (Models.Animal & { withdrawalDays?: number })[] = [];

  get tags() {
    const tags: Models.Tag[] = this.$store.state.scan.tags;
    this.sgtins = tags.map((t) => t.sgtin);
    return this.$store.state.scan.tags;
  }

  @Watch("sgtins")
  onSgtinsChanged(val: string[], oldVal: string[]) {
    const newSgtins = lodash.difference(val, oldVal); //only lookup new sgtins, otherwise it seems that the system looks up too many of the same animals
    const addAnimal = async (sgtin: string) => {
      const animal = await this.$store.dispatch("data/getAnimal", sgtin);
      if (animal) {
        animal.withdrawalDays = withdrawalDaysFromToday(animal);
        this.animals.push(animal);
      }
    };
    for (const sgtin of newSgtins) addAnimal(sgtin);

    //filter out any animals with sgtin no longer being used
    this.animals = this.animals.filter((a) => this.sgtins.find((sgtin) => a.sgtin === sgtin) !== undefined);
  }

  @Watch("batchNumber")
  async onBatchNumberChanged(val: any, oldVal: any) {
    this.$store.commit("groupVaccination/batchNumber", val);
  }

  @Watch("batchQuantity")
  async onBatchQuantityChanged(val: any, oldVal: any) {
    this.$store.commit("groupVaccination/batchQuantity", parseInt(val));
  }

  @Watch("kraalId")
  async onKraalIdChanged(val: any, oldVal: any) {
    this.$store.commit("groupVaccination/kraalId", val);
  }

  @Watch("notes")
  async onNotesChanged(val: any, oldVal: any) {
    this.$store.commit("groupVaccination/notes", val);
  }

  @Watch("useAverageMass")
  onUseAverageMassChanged(val: any) {
    //this.setAppliedVaccination;
    this.selectedVaccination = null;
  }

  @Watch("averageMass")
  onAverageMassChanged(val: any) {
    this.$store.commit("groupVaccination/averageMass", this.getAverageMass());
    this.determineAppliedVaccination();
  }

  @Watch("selectedVaccination")
  onSelectedVaccinationChanged(vaccination?: Models.Vaccination) {
    console.log("onSelectedVaccinationChanged: " + JSON.stringify(vaccination));
    this.determineAppliedVaccination();
  }

  async mounted() {
    window.scrollTo({ top: 0 });
    this.$store.commit("scan/setAppMode", "group-vaccination");

    if (this.$store.state.scan.appState === "saving") {
      this.$store.commit("scan/flagError");
    }
    this.$store.dispatch("power/setPowerMode", "high");

    this.kraalIds = await this.$store.dispatch("data/getKraalIds");

    //await this.$store.dispatch("data/linkSgtinsToAnimalsFromDb", this.tags);

    if (this.tags.length > 0) {
      this.batchQuantity = this.$store.state.groupVaccination.batchQuantity;
      this.batchNumber = this.$store.state.groupVaccination.batchNumber;
      this.notes = this.$store.state.groupVaccination.notes;
      this.kraalId = this.$store.state.groupVaccination.kraalId || "";
      const savedAverageMass = this.$store.state.groupVaccination.averageMass;
      this.averageMass = savedAverageMass !== undefined ? `${savedAverageMass}` : "";
      this.useAverageMass = savedAverageMass !== undefined;
    } else {
      this.$store.commit("groupVaccination/batchQuantity", "");
      this.$store.commit("groupVaccination/batchNumber", "");
      this.$store.commit("groupVaccination/kraalId", "");
      this.$store.commit("groupVaccination/notes", "");
      this.$store.commit("groupVaccination/averageMass", undefined);
      this.averageMass = "";
      this.useAverageMass = false;
    }

    this.setAppliedVaccination(null);
  }

  async onSave() {
    await this.$store.dispatch("groupVaccination/save");
    this.isUiLocked = true;
  }

  setAppliedVaccination(appliedVaccination: Models.AppliedVaccination | null | undefined) {
    if (appliedVaccination === null) {
      appliedVaccination = undefined;
    }
    this.$store.commit("groupVaccination/appliedVaccination", appliedVaccination);
    this.appliedDoses = appliedVaccination?.appliedDoses ?? [];
  }

  determineAppliedVaccination() {
    if (!this.selectedVaccination) {
      this.setAppliedVaccination(undefined);
      return;
    }

    try {
      const appliedVaccination = new Models.AppliedVaccination();
      appliedVaccination.vaccination = this.selectedVaccination;
      appliedVaccination.appliedDoses = [];

      const isMassValid = this.isAverageMassValid();
      const mass = this.getAverageMass();

      for (const dose of this.selectedVaccination.doses) {
        const appliedDose = new Models.AppliedDose();
        if (!dose.item) throw Error(`${this.selectedVaccination.description} has a dose that has no dispensary item`);
        appliedDose.item = dose.item;
        appliedDose.unit = dose.unit;
        if (!dose.amount) {
          throw Error(`${this.selectedVaccination.description} has a dose that does not have amount set`);
        }
        if (dose.type === "cc-mass-dependent") {
          if (!dose.kgPerDose) throw Error(`${this.selectedVaccination.description} has a dose that is cc-mass-dependent but does not have kgPerDose set`);
          if (!isMassValid) throw Error(`${this.selectedVaccination.description} required a valid mass`);
          //Math.ceil might not work for other feedlots. So far this is good for Sernick. We might need this to be configurable
          appliedDose.actualDose = Math.ceil(((mass as number) / dose.kgPerDose) * dose.amount);
        } else {
          appliedDose.actualDose = dose.amount;
        }
        appliedVaccination.appliedDoses.push(appliedDose);
      }
      this.setAppliedVaccination(appliedVaccination);
    } catch (err) {
      this.setAppliedVaccination(undefined);
      console.error(err);
    }
  }
}
